import { useState, useEffect, useCallback } from "react";
import {
  Users, Phone, UserCheck, RefreshCw, AlertTriangle, ChevronRight,
  Clock, ArrowRight, X, Calendar, CheckCircle, CalendarDays,
  XCircle, History, Save, MapPin, User, AlertCircle, Edit3, Car, Navigation, CalendarPlus, Bell, Plus, FileText
} from "lucide-react";
import RepositionnerRdvView from "./RepositionnerRdvView";
import AddRappelModal from "./AddRappelModal";
import LogementInfoSection from "./LogementInfoSection";
import MandataireANAHSection from "./MandataireANAHSection";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Motifs prédéfinis par type
const MOTIFS_PREDEFINIS = {
  perte: [
    "Budget insuffisant",
    "Délai trop long",
    "A trouvé un autre prestataire",
    "Projet abandonné",
    "Déménagement",
    "Décès",
    "Problème de santé",
    "Ne répond plus",
    "Autre"
  ],
  rappel: [
    "Client indisponible",
    "Doit consulter sa famille",
    "Attend des documents",
    "En réflexion",
    "Autre"
  ]
};

export default function ClientPipelineDetail({ clientId, onClose, onStatusChanged }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [clientFieldsConfig, setClientFieldsConfig] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedNextEtape, setSelectedNextEtape] = useState(null);
  const [showRappelModal, setShowRappelModal] = useState(false);
  const [rappelData, setRappelData] = useState({ date: "", heure: "09:00", motif: "" });
  const [addingRappel, setAddingRappel] = useState(false);
  const [showRepositionView, setShowRepositionView] = useState(false);
  
  // Système d'onglets
  const [activeTab, setActiveTab] = useState('infos'); // 'infos', 'historique', 'rappels'
  const [showAddRappelModal, setShowAddRappelModal] = useState(false);

  const loadClient = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/pipeline/client/${encodeURIComponent(clientId)}`);
      if (resp.ok) {
        const data = await resp.json();
        setClient(data);
      }
    } catch (e) {
      console.error("Erreur chargement client:", e);
    }
  }, [clientId]);

  const loadConfig = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/pipeline/config`);
      if (resp.ok) {
        const data = await resp.json();
        setPipelineConfig(data);
      }
    } catch (e) {
      console.error("Erreur chargement config:", e);
    }
  }, []);

  const loadClientFieldsConfig = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/config/client-fields`);
      if (resp.ok) {
        const data = await resp.json();
        setClientFieldsConfig(data);
      }
    } catch (e) {
      console.error("Erreur chargement config champs client:", e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadClient(), loadConfig(), loadClientFieldsConfig()]);
      setLoading(false);
    };
    load();
  }, [loadClient, loadConfig, loadClientFieldsConfig]);

  // Note: L'ancien modal de repositionnement a été remplacé par RepositionnerRdvView

  const updateEtape = async () => {
    if (!selectedNextEtape) return;
    
    setUpdating(true);
    try {
      // Pour l'instant, on met à jour uniquement en local
      // TODO: Implémenter l'endpoint POST /pipeline/client/{client_id}/etape
      alert("Fonctionnalité de changement d'étape en cours de développement");
      setShowStatusModal(false);
      setSelectedNextEtape(null);
    } catch (e) {
      console.error("Erreur mise à jour:", e);
      alert("Erreur lors de la mise à jour");
    }
    setUpdating(false);
  };

  // Note: repositionRdv a été déplacé vers RepositionnerRdvView

  const addRappel = async () => {
    if (!rappelData.date || !rappelData.motif) {
      alert("Veuillez remplir la date et le motif");
      return;
    }
    
    setAddingRappel(true);
    try {
      // Récupérer l'utilisateur connecté depuis localStorage
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      
      const resp = await authFetch(`${API}/pipeline/client/${encodeURIComponent(clientId)}/rappel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_rappel: rappelData.date,
          heure_rappel: rappelData.heure || "09:00",
          motif: rappelData.motif,
          user: authUser.email || authUser.name || "Utilisateur"
        })
      });
      
      if (resp.ok) {
        // Recharger les données du client
        await loadClient();
        setShowRappelModal(false);
        setRappelData({ date: "", heure: "09:00", motif: "" });
      } else {
        const error = await resp.json();
        alert(`Erreur: ${error.detail || "Impossible d'ajouter le rappel"}`);
      }
    } catch (e) {
      console.error("Erreur ajout rappel:", e);
      alert("Erreur lors de l'ajout du rappel");
    }
    setAddingRappel(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-slate-900 rounded-xl p-8">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-slate-900 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-lg">Client non trouvé</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-700 rounded-lg">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const etapesMap = pipelineConfig?.etapes_map || {};
  const currentEtapeInfo = etapesMap[client.etape_id] || {};
  const nextEtapes = client.etapes_possibles || [];
  
  // Vérifier si les données requises pour prendre un RDV sont présentes
  const hasRequiredDataForRdv = () => {
    const requiredFields = {
      nom: client.nom?.trim(),
      prenom: client.prenom?.trim(),
      telephone: client.telephone?.trim() || client.telephones?.[0]?.trim(),
      adresse: client.adresse?.trim(),
      dpt: client.dpt?.trim() || client.departement?.trim()
    };
    
    // Retourne un objet avec le status et les champs manquants
    const missingFields = [];
    if (!requiredFields.nom) missingFields.push("Nom");
    if (!requiredFields.prenom) missingFields.push("Prénom");
    if (!requiredFields.telephone) missingFields.push("Téléphone");
    if (!requiredFields.adresse) missingFields.push("Adresse");
    if (!requiredFields.dpt) missingFields.push("Département");
    
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };
  
  const rdvRequirements = hasRequiredDataForRdv();
  const canTakeRdv = rdvRequirements.isValid && !client.date_r1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: currentEtapeInfo.color || "#6366F1" }}
            >
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{client.nom} {client.prenom}</h2>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Dpt {client.dpt}
                </span>
                {client.telephone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {client.telephone}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="close-detail-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Onglets */}
        <div className="px-6 border-b border-slate-700 flex items-center gap-1 bg-slate-800/50">
          <button
            onClick={() => setActiveTab('infos')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'infos' 
                ? 'border-indigo-500 text-indigo-400' 
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="tab-infos"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Informations
          </button>
          <button
            onClick={() => setActiveTab('historique')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'historique' 
                ? 'border-purple-500 text-purple-400' 
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="tab-historique"
          >
            <History className="w-4 h-4 inline mr-2" />
            Historique
            {client.historique?.length > 0 && (
              <span className="ml-2 bg-purple-500/20 text-purple-400 text-xs px-1.5 py-0.5 rounded">
                {client.historique.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('rappels')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'rappels' 
                ? 'border-amber-500 text-amber-400' 
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
            data-testid="tab-rappels"
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Rappels
            {client.date_rappel && (
              <span className="ml-2 bg-amber-500/20 text-amber-400 text-xs px-1.5 py-0.5 rounded">
                1
              </span>
            )}
          </button>
          
          {/* Bouton rapide pour ajouter un rappel */}
          <button
            onClick={() => setShowAddRappelModal(true)}
            className="ml-auto px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm flex items-center gap-1.5 transition-colors"
            data-testid="quick-add-rappel-btn"
          >
            <Plus className="w-4 h-4" />
            Rappel
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Onglet Informations */}
          {activeTab === 'infos' && (
            <>
              {/* Étape actuelle */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    Étape actuelle
              </h3>
            </div>
            
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${currentEtapeInfo.color || '#6366F1'}30` }}
              >
                <Users className="w-6 h-6" style={{ color: currentEtapeInfo.color || '#6366F1' }} />
              </div>
              <div className="flex-1">
                <p className="text-lg font-medium">{client.etape_nom || currentEtapeInfo.nom || client.etape_id}</p>
                <p className="text-sm text-slate-400">{currentEtapeInfo.description}</p>
              </div>
              {client.date_etape && (
                <div className="text-right text-sm text-slate-400">
                  <p>Date</p>
                  <p className="font-medium text-white">{client.date_etape}</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions - Changer l'étape */}
          {nextEtapes.length > 0 && (
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <ArrowRight className="w-5 h-5 text-indigo-400" />
                Étapes possibles
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {nextEtapes.map((etape) => {
                  if (!etape || !etape.id) return null;
                  const isNegative = etape.id.includes("perdu");
                  
                  return (
                    <button
                      key={etape.id}
                      onClick={() => {
                        setSelectedNextEtape(etape.id);
                        setShowStatusModal(true);
                      }}
                      className={`p-3 rounded-lg text-left transition-all ${
                        isNegative 
                          ? "bg-red-500/10 hover:bg-red-500/20 border border-red-500/30" 
                          : "bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30"
                      }`}
                      data-testid={`etape-action-${etape.id}`}
                    >
                      <p className={`font-medium ${isNegative ? "text-red-400" : "text-indigo-400"}`}>
                        {etape.nom}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{etape.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Informations */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-blue-400" />
                Intervenants
              </h3>
              <div className="space-y-2 text-sm">
                {client.telepro && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Télépro</span>
                    <span>{client.telepro}</span>
                  </div>
                )}
                {client.technicien && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Technicien</span>
                    <span>{client.technicien}</span>
                  </div>
                )}
                {client.source && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Source</span>
                    <span>{client.source}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  Dates clés
                </h3>
                <div className="flex gap-2">
                  {/* Bouton Prendre RDV - visible si pas de date R1 */}
                  {!client.date_r1 && (
                    <div className="relative group">
                      <button
                        onClick={() => canTakeRdv && setShowRepositionView(true)}
                        disabled={!canTakeRdv}
                        className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-all ${
                          canTakeRdv 
                            ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400" 
                            : "bg-slate-700/50 text-slate-500 cursor-not-allowed"
                        }`}
                        data-testid="prendre-rdv-btn"
                      >
                        <CalendarPlus className="w-3 h-3" />
                        Prendre RDV
                      </button>
                      {/* Tooltip pour les champs manquants */}
                      {!canTakeRdv && rdvRequirements.missingFields.length > 0 && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                          <div className="bg-slate-800 border border-slate-600 rounded-lg p-2 text-xs whitespace-nowrap shadow-xl">
                            <p className="text-amber-400 font-medium mb-1">Champs manquants :</p>
                            {rdvRequirements.missingFields.map((field) => (
                              <p key={field} className="text-slate-300">• {field}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Bouton Repositionner - visible si déjà un RDV */}
                  {(client.date_r1 || client.etape_id === 'lead_r1_pris' || client.etape_id === 'confirmer_rdv') && (
                    <button
                      onClick={() => setShowRepositionView(true)}
                      className="px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded text-xs flex items-center gap-1"
                      data-testid="repositionner-rdv-btn"
                    >
                      <Edit3 className="w-3 h-3" />
                      Repositionner
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {client.date_prise_rdv && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Prise RDV</span>
                    <span>{client.date_prise_rdv}</span>
                  </div>
                )}
                {client.date_r1 ? (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">R1</span>
                    <span className="font-medium text-purple-400">{client.date_r1}</span>
                  </div>
                ) : (
                  <div className="text-center py-2 text-slate-500 text-xs">
                    Aucun RDV planifié
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section des champs configurables */}
          {clientFieldsConfig && client.client_details && (
            <div className="space-y-4">
              {Object.entries(clientFieldsConfig.categories || {}).map(([catId, category]) => {
                // Ne montrer que les catégories qui ont des champs activés avec des valeurs
                const enabledFieldsWithValues = category.fields.filter(
                  field => field.enabled && client.client_details[field.id]
                );
                
                if (enabledFieldsWithValues.length === 0) return null;
                
                // Icônes pour chaque catégorie
                const categoryIcons = {
                  identite: <User className="w-5 h-5 text-blue-400" />,
                  contact: <Phone className="w-5 h-5 text-green-400" />,
                  logement: <MapPin className="w-5 h-5 text-amber-400" />,
                  commercial: <UserCheck className="w-5 h-5 text-indigo-400" />,
                  rdv: <Calendar className="w-5 h-5 text-purple-400" />,
                  administratif: <AlertCircle className="w-5 h-5 text-cyan-400" />,
                  notes: <History className="w-5 h-5 text-orange-400" />
                };
                
                return (
                  <div key={catId} className="bg-slate-800/50 rounded-xl p-4">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      {categoryIcons[catId] || <AlertCircle className="w-5 h-5 text-slate-400" />}
                      {category.icon} {category.label}
                    </h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      {enabledFieldsWithValues.map(field => (
                        <div key={field.id} className="flex justify-between py-1 border-b border-slate-700/30">
                          <span className="text-slate-400">{field.label}</span>
                          <span className="text-white font-medium text-right max-w-[60%] truncate" title={client.client_details[field.id]}>
                            {client.client_details[field.id]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Informations Logement */}
          <LogementInfoSection clientId={clientId} />
          
          {/* Mandataire ANAH */}
          <MandataireANAHSection clientId={clientId} />

          {/* Historique */}
          {client.historique?.length > 0 && (
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-amber-400" />
                Historique
              </h3>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {client.historique.slice().reverse().map((entry, idx) => {
                  const etapeInfo = etapesMap[entry.etape];
                  return (
                    <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-slate-900/50">
                      <div 
                        className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: etapeInfo?.color || "#6B7280" }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{etapeInfo?.nom || entry.etape}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(entry.date).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
            </>
          )}

          {/* Onglet Historique */}
          {activeTab === 'historique' && (
            <div className="space-y-4">
              <div className="bg-slate-800/50 rounded-xl p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-purple-400" />
                  Historique complet
                </h3>
                {client.historique?.length > 0 ? (
                  <div className="space-y-3">
                    {client.historique.slice().reverse().map((entry, idx) => {
                      const etapeInfo = etapesMap[entry.etape];
                      return (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                          <div 
                            className="w-4 h-4 rounded-full mt-0.5 flex-shrink-0"
                            style={{ backgroundColor: etapeInfo?.color || "#6B7280" }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white">{etapeInfo?.nom || entry.etape}</p>
                            {entry.details && (
                              <p className="text-sm text-slate-400 mt-1">{entry.details}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(entry.date).toLocaleString('fr-FR')}
                              {entry.user && ` • ${entry.user}`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-8">Aucun historique disponible</p>
                )}
              </div>
            </div>
          )}

          {/* Onglet Rappels */}
          {activeTab === 'rappels' && (
            <div className="space-y-4">
              {/* Rappel actuel (depuis le lead) */}
              {client.date_rappel && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <h3 className="font-semibold flex items-center gap-2 mb-3 text-amber-400">
                    <Bell className="w-5 h-5" />
                    Rappel planifié
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold text-white">
                        {client.date_rappel}
                        {client.heure_rappel && (
                          <span className="text-amber-400 ml-2">à {client.heure_rappel}</span>
                        )}
                      </p>
                      {client.notes && (
                        <p className="text-sm text-slate-400 mt-1">"{client.notes}"</p>
                      )}
                      {client.telepro_rappel && (
                        <p className="text-xs text-slate-500 mt-1">Assigné à: {client.telepro_rappel}</p>
                      )}
                    </div>
                    <Phone className="w-8 h-8 text-amber-400" />
                  </div>
                </div>
              )}

              {/* Liste des rappels historiques */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    Rappels ({client.rappels?.length || 0})
                  </h3>
                  <button
                    onClick={() => setShowAddRappelModal(true)}
                    className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm flex items-center gap-1"
                    data-testid="add-rappel-from-tab-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nouveau rappel
                  </button>
                </div>
                {client.rappels?.length > 0 ? (
                  <div className="space-y-2">
                    {client.rappels.map((rappel, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg flex items-center justify-between border ${
                          rappel.fait 
                            ? "bg-slate-900/30 border-slate-700" 
                            : "bg-orange-500/10 border-orange-500/30"
                        }`}
                      >
                        <div>
                          <p className={`font-medium ${rappel.fait ? "text-slate-500 line-through" : "text-white"}`}>
                            {rappel.date} {rappel.heure && <span className="text-orange-400">à {rappel.heure}</span>}
                          </p>
                          <p className="text-sm text-slate-400">{rappel.motif}</p>
                          {rappel.user && (
                            <p className="text-xs text-slate-500 mt-0.5">Par {rappel.user}</p>
                          )}
                        </div>
                        {rappel.fait ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-orange-400" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-4">Aucun rappel historique</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/30 flex justify-between items-center">
          <div className="text-sm text-slate-400">
            ID: {client.client_id?.substring(0, 30)}...
            {client.updated_at && ` • MAJ: ${new Date(client.updated_at).toLocaleString('fr-FR')}`}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Modal changement d'étape */}
      {showStatusModal && selectedNextEtape && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              Changer l'étape vers: {etapesMap[selectedNextEtape]?.nom || selectedNextEtape}
            </h3>
            
            <p className="text-slate-400 text-sm mb-4">
              Cette fonctionnalité écrira le changement dans MongoDB et le Google Sheet.
            </p>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedNextEtape(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={updateEtape}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg flex items-center justify-center gap-2"
              >
                {updating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajout rappel */}
      {showRappelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Ajouter un rappel</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Date du rappel</label>
                  <input
                    type="date"
                    value={rappelData.date}
                    onChange={(e) => setRappelData({...rappelData, date: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="rappel-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Heure</label>
                  <input
                    type="time"
                    value={rappelData.heure}
                    onChange={(e) => setRappelData({...rappelData, heure: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="rappel-heure-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Motif</label>
                <select
                  value={rappelData.motif}
                  onChange={(e) => setRappelData({...rappelData, motif: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  data-testid="rappel-motif-select"
                >
                  <option value="">Sélectionner...</option>
                  {MOTIFS_PREDEFINIS.rappel.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRappelModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                disabled={addingRappel}
              >
                Annuler
              </button>
              <button
                onClick={addRappel}
                disabled={addingRappel}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                data-testid="confirm-rappel-btn"
              >
                {addingRappel && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vue plein écran pour repositionner le RDV */}
      {showRepositionView && client && (
        <RepositionnerRdvView
          client={client}
          onClose={() => setShowRepositionView(false)}
          onSuccess={() => {
            loadClient();
            if (onStatusChanged) onStatusChanged();
          }}
        />
      )}

      {/* Modal nouveau rappel avec recherche client */}
      <AddRappelModal
        isOpen={showAddRappelModal}
        onClose={() => setShowAddRappelModal(false)}
        onSuccess={() => {
          loadClient();
          setShowAddRappelModal(false);
        }}
        authUser={JSON.parse(localStorage.getItem('auth_user') || '{}')}
        preSelectedClient={client ? {
          lead_id: client.client_id || `row_${client.row_number}`,
          nom: client.nom,
          prenom: client.prenom || '',
          telephone: client.telephone,
          departement: client.dpt,
          email: client.email
        } : null}
      />
    </div>
  );
}
